import {
  FittedModelResult,
  GenModelData,
  RegressionModelDetailData,
  useCmaAssetClassModelDataRetrieveQuery,
  useCmaGenerateModelVersionListQuery,
} from "@app/api/carApi.generated";
import {
  ExcelColumn,
  ExcelDataFormat,
  ExcelTable,
  useExcelExport,
} from "@app/hooks/useExcelExport";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatDate, isDefined, openInNewTab } from "@app/utils";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import {
  CarSelectOption,
  CarSelectOptionFieldProps,
} from "@app/components/Inputs";
import {
  AssetClassLoadType,
  useAssetClasses,
} from "@app/features/assets/useAssetClasses";
import { useAssetClassSelector } from "@app/hooks/useAssetClassSelector";
import { simulationPages } from "@app/const/route.const";
import { CarStatBoxItem } from "@app/components/StatBox";
import { formatISO } from "date-fns";

enum StrategyType {
  linearRegressionBased = "linear_regression_based",
  meanBased = "mean_based",
  polynomialRegressionBased = "polynomial_regression_based",
}

interface RegressionResultItem {
  label: string;
  data?: RegressionModelDetailData;
  fittedModelResult?: FittedModelResult;
  statItems: CarStatBoxItem[];
}

interface UseSimulationModelsModelsParams {
  isStatic: boolean;
  defAssetClassId?: string;
  defModelVersionId?: string;
  defRegressionResultItemIndex?: number;
}

export const useSimulationModelsModels = (
  params: UseSimulationModelsModelsParams,
) => {
  const assetClasses = useAssetClasses({
    type: AssetClassLoadType.allFirm,
  });

  const [startDate, setStartDate] = useState<string | undefined>();
  const [endDate, setEndDate] = useState<string | undefined>(
    formatISO(new Date(), { representation: "date" }),
  );

  const _acSelector = useAssetClassSelector({
    assetClasses,
    defAssetClassId: params.defAssetClassId,
  });

  useEffect(() => {
    if (!startDate) {
      const cmaDataStartDate = assetClasses.items.find(
        (ac) => ac.level4Id === _acSelector.value,
      )?.cmaDataStartDate;

      setStartDate(
        cmaDataStartDate
          ? formatISO(cmaDataStartDate, {
              representation: "date",
            })
          : undefined,
      );
    }
  }, [_acSelector.value, startDate, assetClasses.items]);

  // patching onChange to reset startDate
  const assetClassSelector = useMemo<typeof _acSelector>(
    () => ({
      ..._acSelector,
      onChange: (value: React.SetStateAction<string | undefined>) => {
        _acSelector.onChange(value);

        const cmaDataStartDate = assetClasses.items.find(
          (ac) => ac.level4Id === value,
        )?.cmaDataStartDate;

        setStartDate(
          cmaDataStartDate
            ? formatISO(cmaDataStartDate, {
                representation: "date",
              })
            : undefined,
        );
      },
    }),
    [_acSelector, assetClasses.items],
  );

  const excelExport = useExcelExport();

  const modelVersions = useCmaGenerateModelVersionListQuery({
    id: "", // todo fix in swagger
  });

  const [modelVersionId, setModelVersionId] = useState<string | undefined>(
    params.defModelVersionId,
  );

  useEffect(() => {
    if (!modelVersionId) {
      setModelVersionId(modelVersions.data?.at(0)?.id);
    }
  }, [modelVersionId, modelVersions]);

  const [regressionResultItemIndex, setRegressionResultItemIndex] = useState<
    number | undefined
  >(params.defRegressionResultItemIndex ?? 0);

  // useEffect(() => {
  //   const acItem = assetClasses.items.find(
  //     (ac) => ac.level4Id === assetClassSelector.value,
  //   );
  //   setStartDate(
  //     acItem?.cmaDataStartDate
  //       ? formatISO(acItem.cmaDataStartDate, {
  //           representation: "date",
  //         })
  //       : undefined,
  //   );
  // }, [assetClasses.items, assetClassSelector.value]);

  const assetClassModelData = useCmaAssetClassModelDataRetrieveQuery(
    {
      assetClassId: assetClassSelector.value ?? "",
      generateModelVersionId: modelVersionId ?? "",
      startDate,
      endDate,
    },
    {
      skip: !assetClassSelector.value || !modelVersionId || !startDate,
    },
  );

  const modelData = params.isStatic
    ? assetClassModelData.data?.static
    : assetClassModelData.data?.dynamic;

  return useMemo(() => {
    const calculationDataDetails = modelData?.calculation_data?.details;

    const getExcelTable = (name: string, items: GenModelData[]): ExcelTable => {
      const rowKeys = Array.from(
        new Set(items.flatMap((i) => Object.keys(i?.data ?? {}))),
      ).sort((a, b) => a.localeCompare(b));

      return {
        name,
        columns: [
          {
            label: "",
            format: ExcelDataFormat.general,
            fraction: 1,
            charWidth: 14,
          },
          ...items.map<ExcelColumn>((i) => {
            return {
              label: i.name,
              pendoClassName: pendoClasses.simulationModelsModels(i.name),
              format:
                typeof Object.values(i.data ?? {}).at(0) === "boolean"
                  ? ExcelDataFormat.boolean
                  : ExcelDataFormat.float,
              decimalOptions: {
                decimalPlaces: 6,
                forceShowDecimals: true,
              },
            };
          }),
        ],

        rows: rowKeys.map((key) => [
          formatDate(key),
          ...items.map(
            (c) =>
              (c.data as Record<string, unknown> | undefined)?.[key] ?? null,
          ),
        ]),
      };
    };

    const tables =
      modelData?.model_input_data?.map((i) =>
        getExcelTable(i.description, (i.data_fields ?? []) as GenModelData[]),
      ) ?? [];

    const handleDownloadPdf = () => {
      const assetClassId = assetClassSelector.value;
      const isStatic = params.isStatic;

      if (
        isDefined(assetClassId) &&
        isDefined(modelVersionId) &&
        isDefined(regressionResultItemIndex)
      ) {
        openInNewTab(
          simulationPages.getPdfModelsModelsReportUrl({
            assetClassId,
            isStatic,
            modelVersionId,
            regressionResultItemIndex,
          }),
        );
      }
    };

    const handleDownloadData = () => {
      excelExport.exportAllExcel({
        fileName: `Simulation Models - ${assetClassSelector.selectedItem?.name}`,
        tables,
      });
    };

    const regressionDetails = calculationDataDetails?.regression_details;
    const strategyType = (modelData?.calculation_data.strategy_type ??
      StrategyType.meanBased) as StrategyType;

    const regressionResultItems: RegressionResultItem[] | undefined =
      regressionDetails?.map((i) => {
        const statItems: CarStatBoxItem[] = [];
        // if (strategyType === StrategyType.linearRegressionBased) {
        //   statItems.push({
        //     label: "Beta",
        //     value: i.beta,
        //     format: ExcelDataFormat.float,
        //     decimalOptions: {
        //       decimalPlaces: 6,
        //       forceShowDecimals: true,
        //     },
        //   });
        // } else if (strategyType === StrategyType.polynomialRegressionBased) {
        //   statItems.push(
        //     {
        //       label: "Beta 2",
        //       value: i.coefficients?.at(1),
        //       format: ExcelDataFormat.float,
        //       decimalOptions: {
        //         decimalPlaces: 6,
        //         forceShowDecimals: true,
        //       },
        //     },
        //     {
        //       label: "Beta 1",
        //       value: i.coefficients?.at(0),
        //       format: ExcelDataFormat.float,
        //       decimalOptions: {
        //         decimalPlaces: 6,
        //         forceShowDecimals: true,
        //       },
        //     },
        //   );
        // }

        statItems.push(
          {
            label: "Model",
            equation: i.equation_katex,
            format: ExcelDataFormat.float,
            decimalOptions: {
              decimalPlaces: 6,
              forceShowDecimals: true,
            },
          },
          {
            label: "R Squared",
            value: i.r_squared,
            format: ExcelDataFormat.float,
            decimalOptions: {
              decimalPlaces: 6,
              forceShowDecimals: true,
            },
          },
        );

        return {
          label: i.key,
          data: i,
          statItems,
        };
      });

    if (regressionResultItems && modelData?.fitted_model_data) {
      regressionResultItems.push({
        label: modelData.fitted_model_data.key,
        fittedModelResult: modelData.fitted_model_data,
        statItems: [],
      });
    }

    const getGeneralStatItems = () => {
      // common for all strategy types
      const result: CarStatBoxItem[] = [
        {
          label: modelData?.model_statistics.mu_label ?? "",
          value: (modelData?.model_statistics.mu ?? 0) * 100,
          format: ExcelDataFormat.float,
          decimalOptions: {
            decimalPlaces: 6,
            forceShowDecimals: true,
          },
        },
        {
          label: modelData?.model_statistics.sigma_label ?? "",
          value: (modelData?.model_statistics.sigma ?? 0) * 100,
          format: ExcelDataFormat.float,
          decimalOptions: {
            decimalPlaces: 6,
            forceShowDecimals: true,
          },
        },
        {
          label: "Trend Slope",
          value: modelData?.trend_growth,
          format: ExcelDataFormat.percent,
          decimalOptions: {
            decimalPlaces: 2,
            forceShowDecimals: true,
          },
        },
      ];

      if (strategyType === StrategyType.linearRegressionBased) {
        result.push(
          {
            label: "Model",
            equation: calculationDataDetails?.regression_result.equation_katex,
            format: ExcelDataFormat.float,
            decimalOptions: {
              decimalPlaces: 6,
              forceShowDecimals: true,
            },
          },
          // {
          //   label: "Beta",
          //   value: calculationDataDetails?.regression_result?.beta,
          //   format: ExcelDataFormat.float,
          //   decimalOptions: {
          //     decimalPlaces: 6,
          //     forceShowDecimals: true,
          //   },
          // },
          // {
          //   label: "Y Intercept",
          //   value: calculationDataDetails?.regression_result?.intercept,
          //   format: ExcelDataFormat.float,
          //   decimalOptions: {
          //     decimalPlaces: 6,
          //     forceShowDecimals: true,
          //   },
          // },
        );
      } else if (strategyType === StrategyType.polynomialRegressionBased) {
        result.push(
          {
            label: "Beta 2",
            value:
              calculationDataDetails?.regression_result?.coefficients?.at(1),
            format: ExcelDataFormat.float,
            decimalOptions: {
              decimalPlaces: 6,
              forceShowDecimals: true,
            },
          },
          {
            label: "Beta 1",
            value:
              calculationDataDetails?.regression_result?.coefficients?.at(0),
            format: ExcelDataFormat.float,
            decimalOptions: {
              decimalPlaces: 6,
              forceShowDecimals: true,
            },
          },
          {
            label: "Y Intercept",
            value: calculationDataDetails?.regression_result?.intercept,
            format: ExcelDataFormat.float,
            decimalOptions: {
              decimalPlaces: 6,
              forceShowDecimals: true,
            },
          },
        );
      }

      return result;
    };

    const versionSelector: CarSelectOptionFieldProps<string> = {
      options:
        modelVersions.data?.map<CarSelectOption<string>>((i) => ({
          label: `${i.name} (${i.full_version})`,
          value: i.id ?? "",
        })) ?? [],
      value: modelVersionId,
      onChange: setModelVersionId,
    };

    const regressionSelector: CarSelectOptionFieldProps<number> | undefined =
      regressionResultItems
        ? {
            options:
              regressionResultItems.map<CarSelectOption<number>>((i, idx) => ({
                value: idx,
                label: i.label,
              })) ?? [],
            value: regressionResultItemIndex,
            onChange: setRegressionResultItemIndex,
          }
        : undefined;

    const selectedRegressionResult = regressionResultItems?.at(
      regressionResultItemIndex ?? -1,
    );

    return {
      isLoading:
        assetClassSelector.isLoading ||
        assetClassModelData.isLoading ||
        modelVersions.isLoading,
      assetClassSelector,
      versionSelector,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      tables,
      generalStat: getGeneralStatItems(),
      notes: modelData?.calculation_data.description,
      regressionSelector,
      selectedRegressionResult,
      handleDownloadData,
      handleDownloadPdf,
    };
  }, [
    modelData,
    modelVersions.data,
    modelVersions.isLoading,
    modelVersionId,
    regressionResultItemIndex,
    assetClassSelector,
    assetClassModelData.isLoading,
    startDate,
    endDate,
    params.isStatic,
    excelExport,
  ]);
};

export type UseSimulationModelsModels = ReturnType<
  typeof useSimulationModelsModels
>;
